import { Fragment, useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from 'react';
import type { Recipe, RecipeTag } from '@repo/types';
import { RECIPE_DIFFICULTIES, RECIPE_MEALS } from '@repo/types';
import { C } from '@/integrations/whoop-theme';
import { useAuth } from '@/auth/auth-context';
import { getRecipes, getRecipeTags } from '@/recipes/api';
import { RecipeModal } from './recipe-modal';
import './recipes.css';

/**
 * The Recipes page (a port of Recipes.dc.html): a searchable, filterable recipe
 * browser. The cookbook itself comes from the GraphQL `recipes` query; search,
 * filters and sorting all stay client-side — the whole cookbook is a few
 * hundred rows at most. The detail modal (and its create mode, behind the
 * admin New recipe button) lives in recipe-modal.tsx. The root layout already
 * supplies the dark `ww-root` shell and the workspace header.
 */

interface FilterGroup {
  key: string;
  label: string;
  options: string[];
}

/** Group key -> the set of option values the user has switched OFF (excluded). */
type FilterState = Record<string, Set<string>>;

/**
 * Time buckets, shared between the Time filter's options and the per-recipe label
 * so the filter's string always matches what a recipe reports (the design
 * duplicated these literals; a shared constant removes the drift risk).
 */
const TIME_LABELS = {
  short: 'Under 30 min',
  mid: '30–60 min',
  long: '60+ min',
} as const;

/** Minimum card width — the design's "Comfortable" density default. */
const CARD_MIN = 232;

/**
 * Filter groups. Meal/difficulty/time options are fixed @repo/types constants;
 * cuisine and meat come from the DB-backed recipe_tags vocabulary (fetched with
 * the cookbook) so a tag added from the admin form shows up here too — the
 * same table the API's write-path validation checks.
 */
function buildGroups(tags: RecipeTag[]): FilterGroup[] {
  const names = (kind: RecipeTag['kind']) =>
    tags.filter((tag) => tag.kind === kind).map(({ name }) => name);
  return [
    { key: 'protein', label: 'Meat', options: names('protein') },
    { key: 'cuisine', label: 'Cuisine', options: names('cuisine') },
    { key: 'meal', label: 'Meal', options: [...RECIPE_MEALS] },
    { key: 'difficulty', label: 'Difficulty', options: [...RECIPE_DIFFICULTIES] },
    { key: 'time', label: 'Time', options: [TIME_LABELS.short, TIME_LABELS.mid, TIME_LABELS.long] },
  ];
}

export function RecipesPage() {
  const { isAdmin } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[] | null>(null);
  const [tags, setTags] = useState<RecipeTag[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({});
  const [order, setOrder] = useState<'az' | 'za'>('az');
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [servings, setServings] = useState<number | null>(null);
  const [spice, setSpice] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRecipes(null);
    setLoadFailed(false);
    // The tag vocabulary drives the filters and the create form's pickers, so
    // it loads (and fails) together with the cookbook.
    Promise.all([getRecipes(), getRecipeTags()])
      .then(([recipeData, tagData]) => {
        if (cancelled) return;
        setRecipes(recipeData);
        setTags(tagData);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const isChecked = (key: string, value: string) => !filters[key]?.has(value);

  function toggleValue(key: string, value: string) {
    setFilters((prev) => {
      const next = new Set(prev[key] ?? []);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });
  }

  function selectAll(group: FilterGroup) {
    setFilters((prev) => ({ ...prev, [group.key]: new Set() }));
  }

  function clearGroup(group: FilterGroup) {
    setFilters((prev) => ({ ...prev, [group.key]: new Set(group.options) }));
  }

  function resetAll() {
    setFilters({});
    setQuery('');
  }

  function openRecipe(recipe: Recipe) {
    setOpenId(recipe.id);
    setServings(recipe.servings);
    setSpice(recipe.spice);
  }

  function showToast(message: string) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  }

  function handleCreated(created: Recipe) {
    // The list re-sorts on render, so appending keeps the grid ordered.
    setRecipes((prev) => (prev ? [...prev, created] : [created]));
    setFormOpen(false);
    showToast(`Added “${created.title}” to the cookbook.`);
  }

  function handleUpdated(updated: Recipe) {
    setRecipes((prev) =>
      prev ? prev.map((recipe) => (recipe.id === updated.id ? updated : recipe)) : prev,
    );
  }

  /** A tag added from the form's pickers joins the filters, in server order. */
  function handleTagCreated(created: RecipeTag) {
    setTags((prev) =>
      [...prev, created].sort(
        (a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name),
      ),
    );
  }

  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const cookbook = recipes ?? [];
  // Multi-tag groups match when ANY of the recipe's tags is still enabled: with
  // only Italian on, an Italian+American fusion dish should still show.
  const anyChecked = (key: string, values: string[]) =>
    values.some((value) => isChecked(key, value));
  const filtered = cookbook.filter((recipe) => {
    if (!anyChecked('protein', recipe.proteins)) return false;
    if (!anyChecked('cuisine', recipe.cuisines)) return false;
    if (!isChecked('meal', recipe.meal)) return false;
    if (!isChecked('difficulty', recipe.difficulty)) return false;
    if (!isChecked('time', formatTimeLabel(recipe.timeMinutes))) return false;
    if (tokens.length) {
      const haystack =
        `${recipe.title} ${recipe.cuisines.join(' ')} ${recipe.proteins.join(' ')} ${recipe.meal} ${recipe.difficulty} ${formatTimeLabel(recipe.timeMinutes)}`.toLowerCase();
      if (!tokens.every((token) => haystack.includes(token))) return false;
    }
    return true;
  });
  const results = [...filtered].sort((a, b) =>
    order === 'az' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title),
  );

  const openedRecipe = openId ? (cookbook.find((recipe) => recipe.id === openId) ?? null) : null;

  // Mirror integrations-hub-page's <main>: width 100% is load-bearing — RootLayout
  // is a column flex container, and with auto cross-axis margins alone the page
  // shrink-to-fits (~820px) instead of filling the shared 1160px cap.
  return (
    <main style={{ width: '100%', maxWidth: 1160, margin: '0 auto', padding: '34px 28px 96px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 22,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1
            style={{
              font: "700 34px 'Hanken Grotesk',sans-serif",
              margin: '0 0 6px',
              letterSpacing: '-0.02em',
              color: C.text,
            }}
          >
            Recipes
          </h1>
          <p style={{ margin: 0, color: C.text2, font: "400 15px 'Hanken Grotesk',sans-serif" }}>
            The dishes I actually cook — search by name, cuisine, meal, or difficulty, then dial in
            servings and heat for your table.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="ww-btn"
            onClick={() => setFormOpen(true)}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '11px 17px',
              borderRadius: 11,
              border: '1px solid rgba(181,155,255,0.35)',
              background: 'rgba(181,155,255,0.14)',
              color: '#d9ccff',
              font: "600 13.5px 'Hanken Grotesk',sans-serif",
              cursor: 'pointer',
              marginTop: 3,
            }}
          >
            <span style={{ fontSize: 17, lineHeight: 0, marginTop: -1 }}>+</span> New recipe
          </button>
        )}
      </div>

      <div style={{ position: 'relative', marginBottom: 18 }}>
        <SearchIcon />
        <input
          className="ww-input"
          value={query}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          placeholder="Search my recipes, cuisines, meals…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: 58,
            padding: '0 20px 0 52px',
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.1)',
            background: '#141416',
            color: C.text,
            font: "500 17px 'Hanken Grotesk',sans-serif",
            outline: 'none',
            transition: 'border-color 0.15s',
          }}
        />
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 9,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          marginBottom: 22,
        }}
      >
        {buildGroups(tags).map((group) => (
          <FilterMenu
            key={group.key}
            group={group}
            excluded={filters[group.key]}
            isOpen={openFilter === group.key}
            onToggleOpen={() => setOpenFilter((prev) => (prev === group.key ? null : group.key))}
            onSelectAll={() => selectAll(group)}
            onClearGroup={() => clearGroup(group)}
            onToggleValue={(value) => toggleValue(group.key, value)}
          />
        ))}
        <button
          type="button"
          className="ww-btn"
          onClick={resetAll}
          style={{
            padding: '9px 14px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent',
            color: '#c9c7c0',
            font: "600 13px 'Hanken Grotesk',sans-serif",
            cursor: 'pointer',
          }}
        >
          Reset all
        </button>
      </div>

      {openFilter !== null && (
        <div
          onClick={() => setOpenFilter(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 8 }}
        />
      )}

      {loadFailed ? (
        <div
          style={{
            textAlign: 'center',
            padding: '64px 20px',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 16,
            background: '#101012',
          }}
        >
          <div
            style={{ font: "600 17px 'Hanken Grotesk',sans-serif", color: C.text, marginBottom: 6 }}
          >
            Couldn’t load the cookbook
          </div>
          <div
            style={{
              font: "400 14px 'Hanken Grotesk',sans-serif",
              color: C.text2,
              marginBottom: 18,
            }}
          >
            Something went wrong fetching the recipes.
          </div>
          <button
            type="button"
            className="ww-btn"
            onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            style={{
              padding: '9px 16px',
              borderRadius: 10,
              border: '1px solid rgba(181,155,255,0.35)',
              background: 'rgba(181,155,255,0.14)',
              color: '#d9ccff',
              font: "600 13px 'Hanken Grotesk',sans-serif",
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      ) : recipes === null ? (
        <div
          style={{
            textAlign: 'center',
            padding: '64px 20px',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 16,
            background: '#101012',
            color: C.text2,
            font: "400 14px 'Hanken Grotesk',sans-serif",
          }}
        >
          Loading recipes…
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ font: "700 16px 'Hanken Grotesk',sans-serif", color: C.text }}>
                {results.length}
              </span>
              <span style={{ color: C.text2, font: "500 14px 'Hanken Grotesk',sans-serif" }}>
                recipes
              </span>
              {results.length !== recipes.length && (
                <span style={{ color: C.text3, font: "500 13px 'Hanken Grotesk',sans-serif" }}>
                  of {recipes.length}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span
                style={{
                  font: "500 10px 'JetBrains Mono',monospace",
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: C.text3,
                  marginRight: 4,
                }}
              >
                Sort
              </span>
              <button
                type="button"
                className="ww-btn"
                onClick={() => setOrder('az')}
                style={sortButtonStyle(order === 'az')}
              >
                A–Z
              </button>
              <button
                type="button"
                className="ww-btn"
                onClick={() => setOrder('za')}
                style={sortButtonStyle(order === 'za')}
              >
                Z–A
              </button>
            </div>
          </div>

          {results.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill,minmax(${CARD_MIN}px,1fr))`,
                gap: 16,
                alignItems: 'start',
              }}
            >
              {results.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  // The server sends drafts only to the admin; the guard is belt
                  // and braces so a guest never sees the badge even in dev.
                  showDraftBadge={isAdmin && recipe.status === 'draft'}
                  onOpen={() => openRecipe(recipe)}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '64px 20px',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16,
                background: '#101012',
              }}
            >
              <div
                style={{
                  font: "600 17px 'Hanken Grotesk',sans-serif",
                  color: C.text,
                  marginBottom: 6,
                }}
              >
                No recipes match
              </div>
              <div
                style={{
                  font: "400 14px 'Hanken Grotesk',sans-serif",
                  color: C.text2,
                  marginBottom: 18,
                }}
              >
                Try a different search or turn some filters back on.
              </div>
              <button
                type="button"
                className="ww-btn"
                onClick={resetAll}
                style={{
                  padding: '9px 16px',
                  borderRadius: 10,
                  border: '1px solid rgba(181,155,255,0.35)',
                  background: 'rgba(181,155,255,0.14)',
                  color: '#d9ccff',
                  font: "600 13px 'Hanken Grotesk',sans-serif",
                  cursor: 'pointer',
                }}
              >
                Reset search &amp; filters
              </button>
            </div>
          )}
        </>
      )}

      {openedRecipe && (
        <RecipeModal
          mode="view"
          recipe={openedRecipe}
          servings={servings ?? openedRecipe.servings}
          spice={spice ?? openedRecipe.spice}
          onClose={() => setOpenId(null)}
          onServingsChange={setServings}
          onSpiceChange={setSpice}
          onUpdated={handleUpdated}
        />
      )}

      {formOpen && (
        <RecipeModal
          mode="create"
          tags={tags}
          onClose={() => setFormOpen(false)}
          onCreated={handleCreated}
          onTagCreated={handleTagCreated}
        />
      )}

      {toast && (
        <div
          className="rp-pop"
          style={{
            position: 'fixed',
            bottom: 26,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 110,
            background: '#1b1b1e',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 12,
            padding: '12px 18px',
            font: "500 13px 'Hanken Grotesk',sans-serif",
            color: C.text,
            boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
          }}
        >
          {toast}
        </div>
      )}
    </main>
  );
}

function FilterMenu({
  group,
  excluded,
  isOpen,
  onToggleOpen,
  onSelectAll,
  onClearGroup,
  onToggleValue,
}: {
  group: FilterGroup;
  excluded: Set<string> | undefined;
  isOpen: boolean;
  onToggleOpen: () => void;
  onSelectAll: () => void;
  onClearGroup: () => void;
  onToggleValue: (value: string) => void;
}) {
  const offCount = excluded?.size ?? 0;
  const highlighted = offCount > 0 || isOpen;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="ww-btn"
        onClick={onToggleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 13px',
          borderRadius: 10,
          border: `1px solid ${highlighted ? 'rgba(181,155,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
          background: highlighted ? 'rgba(181,155,255,0.1)' : '#141416',
          color: C.text,
          font: "600 13px 'Hanken Grotesk',sans-serif",
          cursor: 'pointer',
        }}
      >
        <span>{group.label}</span>
        <span
          style={{
            font: "600 10px 'JetBrains Mono',monospace",
            letterSpacing: '0.04em',
            color: offCount > 0 ? '#d9ccff' : C.text3,
            padding: '2px 7px',
            borderRadius: 6,
            background: offCount > 0 ? 'rgba(181,155,255,0.16)' : 'rgba(255,255,255,0.05)',
          }}
        >
          {offCount === 0 ? 'All' : `${offCount} off`}
        </span>
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ opacity: 0.55 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div
          className="rp-pop"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            zIndex: 10,
            width: 'max-content',
            minWidth: 210,
            maxWidth: 340,
            background: '#1b1b1e',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 13,
            padding: 13,
            boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 11,
            }}
          >
            <span
              style={{
                font: "500 10px 'JetBrains Mono',monospace",
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: C.text3,
              }}
            >
              {group.label}
            </span>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                className="ww-btn"
                onClick={onSelectAll}
                style={menuActionStyle}
              >
                Select all
              </button>
              <button
                type="button"
                className="ww-btn"
                onClick={onClearGroup}
                style={menuActionStyle}
              >
                Clear
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {group.options.map((option) => {
              const checked = !excluded?.has(option);
              return (
                <button
                  type="button"
                  key={option}
                  className="ww-btn"
                  onClick={() => onToggleValue(option)}
                  style={checked ? chipOnStyle : chipOffStyle}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RecipeCard({
  recipe,
  showDraftBadge,
  onOpen,
}: {
  recipe: Recipe;
  showDraftBadge: boolean;
  onOpen: () => void;
}) {
  const meta = [
    formatTimeLabel(recipe.timeMinutes),
    ...(recipe.calories !== null ? [`${recipe.calories} cal`] : []),
    `${recipe.servings} serv`,
  ];
  return (
    <div
      className="ww-int-card ww-fade-in"
      onClick={onOpen}
      style={{
        cursor: 'pointer',
        background: '#141416',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 14,
        overflow: 'hidden',
        height: 266,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 176,
          flexShrink: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          background: '#111114',
          backgroundImage:
            'repeating-linear-gradient(135deg,rgba(255,255,255,0.018) 0px,rgba(255,255,255,0.018) 1px,transparent 1px,transparent 9px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        {recipe.imageURL !== null ? (
          <img
            src={recipe.imageURL}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <>
            <UtensilsIcon />
            <div
              style={{
                font: "500 10px 'JetBrains Mono',monospace",
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: C.text3,
              }}
            >
              {recipe.cuisines.join(' · ')}
            </div>
          </>
        )}
      </div>
      <div
        style={{
          padding: '10px 13px 11px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            font: "600 15px 'Hanken Grotesk',sans-serif",
            lineHeight: 1.2,
            color: C.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {recipe.title}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            color: C.text2,
            font: "500 11.5px 'Hanken Grotesk',sans-serif",
            marginTop: 4,
            whiteSpace: 'nowrap',
          }}
        >
          {meta.map((segment, index) => (
            <Fragment key={segment}>
              {index > 0 && <span style={{ color: '#3f3e3a' }}>·</span>}
              <span>{segment}</span>
            </Fragment>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 5,
            marginTop: 'auto',
            overflow: 'hidden',
            height: 20,
            alignItems: 'center',
          }}
        >
          {showDraftBadge && <span style={DRAFT_TAG_STYLE}>Draft</span>}
          {[...recipe.cuisines, ...recipe.proteins, recipe.difficulty].map((label) => (
            <span key={label} style={TAG_STYLE}>
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** `m < 30` → "Under 30 min", `m <= 60` → "30–60 min", else "60+ min". */
function formatTimeLabel(minutes: number): string {
  if (minutes < 30) return TIME_LABELS.short;
  if (minutes <= 60) return TIME_LABELS.mid;
  return TIME_LABELS.long;
}

function sortButtonStyle(active: boolean): CSSProperties {
  return {
    padding: '6px 12px',
    borderRadius: 8,
    border: `1px solid ${active ? 'rgba(181,155,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
    background: active ? 'rgba(181,155,255,0.14)' : 'transparent',
    color: active ? '#d9ccff' : C.text2,
    font: "600 12px 'Hanken Grotesk',sans-serif",
    cursor: 'pointer',
  };
}

const TAG_STYLE: CSSProperties = {
  padding: '3px 8px',
  borderRadius: 6,
  background: 'rgba(255,255,255,0.05)',
  color: C.text2,
  font: "600 10.5px 'JetBrains Mono',monospace",
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/** Admin-only card marker for unpublished recipes (mirrors the modal's status tag). */
const DRAFT_TAG_STYLE: CSSProperties = {
  ...TAG_STYLE,
  background: 'rgba(227,198,115,0.14)',
  color: C.warn,
};

const chipOnStyle: CSSProperties = {
  padding: '7px 12px',
  borderRadius: 999,
  border: '1px solid rgba(181,155,255,0.42)',
  background: 'rgba(181,155,255,0.12)',
  color: '#d9ccff',
  font: "600 12.5px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const chipOffStyle: CSSProperties = {
  padding: '7px 12px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'transparent',
  color: C.text3,
  font: "600 12.5px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const menuActionStyle: CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  color: C.text2,
  font: "600 11px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke={C.text3}
      strokeWidth="2"
      strokeLinecap="round"
      style={{
        position: 'absolute',
        left: 20,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function UtensilsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="#3f3e3a"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 3v7a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3" />
      <line x1="6" y1="12" x2="6" y2="21" />
      <path d="M17 3c-1.5 0-3 1.8-3 5 0 2 1 3 2 3h1v10" />
    </svg>
  );
}
